import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Tag,
    Typography,
    Table,
    Input,
    Space,
    Dropdown,
    Button,
    Tooltip,
    App,
    Image
} from 'antd';
import {
    MoreOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    BlockOutlined
} from '@ant-design/icons';
import { listSpecialties, deleteSpecialty } from '../../services/specialties';
import { resolveMedia } from '../../utils/media';

const { Title } = Typography;
const { useApp } = App;

const SpecialtiesList = () => {
    const navigate = useNavigate();
    const { message, modal } = useApp();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchData = useCallback(async (page, pageSize, q) => {
        setLoading(true);
        try {
            const response = await listSpecialties({
                page: page || pagination.current,
                pageSize: pageSize || pagination.pageSize,
                search: q !== undefined ? q : search,
            });

            setData(response.items || []);
            setPagination(prev => ({
                ...prev,
                current: page || prev.current,
                total: response.total
            }));
        } catch (err) {
            console.error(err);
            message.error('No se pudo cargar la lista de especialidades');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, search, message]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (value) => {
        setSearch(value);
        fetchData(1, pagination.pageSize, value);
    };

    const handleMenuAction = (action, record) => {
        switch (action) {
            case 'edit':
                navigate(`/specialties/${record.id}/edit`);
                break;
            case 'delete':
                modal.confirm({
                    title: '¿Eliminar especialidad?',
                    content: `¿Estás seguro de que deseas eliminar "${record.displayName || record.label}"?`,
                    okText: 'Eliminar',
                    okButtonProps: { danger: true },
                    cancelText: 'Cancelar',
                    onOk: async () => {
                        try {
                            await deleteSpecialty(record.id);
                            message.success('Especialidad eliminada correctamente');
                            fetchData();
                        } catch (err) {
                            message.error('No se pudo eliminar la especialidad');
                        }
                    },
                });
                break;
            default:
                break;
        }
    };

    const columns = [
        {
            title: 'Icono',
            dataIndex: 'icon',
            key: 'icon',
            align: 'center',
            render: (icon) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {icon ? (
                        <Image
                            src={resolveMedia(icon)}
                            width={40}
                            height={40}
                            style={{ objectFit: 'contain' }}
                            preview={{ mask: null }}
                        />
                    ) : (
                        <BlockOutlined style={{ fontSize: '24px', color: '#CCC' }} />
                    )}
                </div>
            ),
        },
        {
            title: 'Clave',
            dataIndex: 'name',
            key: 'name',
            render: (val, record) => val || record.value,
            sorter: (a, b) => (a.name || a.value || '').localeCompare(b.name || b.value || ''),
        },
        {
            title: 'Especialidad',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (val, record) => val || record.label,
            sorter: (a, b) => (a.displayName || a.label || '').localeCompare(b.displayName || b.label || ''),
        },
        {
            title: 'Descripción corta',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Estado',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'volcano'}>
                    {isActive ? 'Activo' : 'Inactivo'}
                </Tag>
            ),
        },
        {
            title: 'Orden',
            dataIndex: 'order',
            key: 'order',
            sorter: (a, b) => (a.order || 0) - (b.order || 0),
            align: 'center',
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'center',
            width: 100,
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: [
                            { key: 'edit', icon: <EditOutlined />, label: 'Editar' },
                            { type: 'divider' },
                            { key: 'delete', icon: <DeleteOutlined />, label: 'Eliminar', danger: true },
                        ],
                        onClick: ({ key }) => handleMenuAction(key, record),
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        style={{ fontSize: '16px' }}
                    />
                </Dropdown>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>Especialidades</Title>
                <Tooltip title="Agregar especialidad">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/specialties/add')}
                        style={{
                            backgroundColor: '#1890ff',
                            borderColor: '#1890ff',
                            borderRadius: '8px',
                            height: '40px',
                            fontWeight: 500,
                        }}
                    >
                        Nuevo
                    </Button>
                </Tooltip>
            </div>

            <Card style={{ borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.Search
                        placeholder="Buscar por especialidad o clave..."
                        allowClear
                        onSearch={onSearch}
                        defaultValue={search}
                        style={{ maxWidth: 360, marginBottom: 16 }}
                    />

                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        onChange={(pag) => fetchData(pag.current, pag.pageSize)}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50],
                        }}
                    />
                </Space>
            </Card>
        </div>
    );
};

export default SpecialtiesList;
